<template>
  <SDialog v-model:open="modalVisible">
    <SDialogContent
      class="download-tickets-popup !w-[80%] !max-w-[80%] !max-h-[85vh] !grid-rows-[auto_1fr]"
      :show-close-button="false"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <div class="flex items-center justify-between gap-4">
          <SDialogTitle>{{ $t("completeorder.list-of-tickets") }}</SDialogTitle>
          <div class="flex items-center gap-3">
            <SButton
              v-if="hasInvoiceEnabled"
              type="button"
              variant="outline"
              @click.stop="downloadInvoice"
              :disabled="loading"
              data-cy="download-invoice-button"
            >
              <DownloadIcon />
              {{ $t("button.download-invoice") }}
            </SButton>
            <SButton
              type="button"
              @click.stop="downloadAllTickets"
              :disabled="loading"
              data-cy="download-all-tickets-button"
            >
              <DownloadIcon />
              {{ $t("button.download-all") }}
            </SButton>
            <button
              type="button"
              class="ml-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus:outline-none"
              :aria-label="$t('button.close')"
              @click="closeModal"
            >
              <XIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
      </SDialogHeader>
      <!-- Spinner overlay -->
      <div v-if="loading" class="spinner-overlay">
        <LoaderCircleIcon class="s-icon-spin spinner" />
      </div>
      <div class="overflow-y-auto min-h-0">
        <el-row
          class="sticky top-0 z-10 bg-white"
          style="
            font-weight: 700;
            border-bottom: 1px solid grey;
            padding-bottom: 15px;
            padding-top: 15px;
          "
        >
          <el-col class="column" :span="4">{{
            $t("common.ticket-name")
          }}</el-col>
          <el-col class="column" :span="4">{{
            $t("common.price-name")
          }}</el-col>
          <el-col class="column" :span="4">{{
            $t("common.price-value")
          }}</el-col>
          <el-col class="column" :span="4">
            {{ $t("orders.customer-full-name") }}
          </el-col>
          <el-col class="column" :span="4">{{ $t("common.bar-code") }}</el-col>
          <el-col class="column" :span="4"></el-col>
        </el-row>
        <div v-for="(data, index) in ticektsInfo" :key="String(data.pss.uuid)">
          <el-row
            style="padding-bottom: 15px; padding-top: 15px"
            :style="
              index !== ticektsInfo.length - 1
                ? 'border-bottom: 1px solid grey'
                : ''
            "
          >
            <el-col
              style="text-align: left; font-weight: 700"
              :span="4"
              class="d-flex fl-col fl-j-c column"
            >
              <p>{{ data.pss.categoryName }}</p>
            </el-col>
            <el-col :span="4" class="d-flex fl-col fl-j-c column"
              ><p>{{ data.pss.priceName }}</p></el-col
            >
            <el-col :span="4" class="d-flex fl-col fl-j-c"
              ><p>
                {{ data.pss.priceValue / 100 + " " + currency }}
              </p></el-col
            >
            <el-col :span="4" class="d-flex fl-col fl-j-c column"
              ><p>{{ data.firstName + " " + data.lastName }}</p></el-col
            >
            <el-col :span="4" class="d-flex fl-col fl-j-c"
              ><p>
                {{ data.pss.barCode ? data.pss.barCode : "-" }}
              </p></el-col
            >
            <el-col
              :span="4"
              style="text-align: right; padding-right: 10px"
              class="d-flex fl-col fl-j-c"
            >
              <div class="flex gap-1 justify-end">
                <!-- for downloading badges -->
                <SButton
                  v-if="isMembershipTicket(data.pss)"
                  type="button"
                  variant="outline"
                  size="icon"
                  class="download-button"
                  :disabled="loading"
                  @click="downloadBadge(data.pss)"
                >
                  <IdCardIcon />
                </SButton>
                <!-- for downloading giftcards -->
                <SButton
                  v-if="data.pss.type === PCTypes.GiftCard"
                  type="button"
                  variant="outline"
                  size="icon"
                  class="download-button"
                  :disabled="loading"
                  @click="downloadGiftcard(data.pss)"
                >
                  <IdCardIcon />
                </SButton>
                <!-- for downloading tickets -->
                <SButton
                  type="button"
                  variant="outline"
                  size="icon"
                  class="download-button"
                  :disabled="loading"
                  @click.stop="downloadTicket(data.pss)"
                >
                  <DownloadIcon />
                </SButton>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import {
  BookingStatus,
  IProductSlotState,
  PSSType,
} from "@/types/db/product-slot-state";
import BookingModule from "@/store/modules/booking";
import ModalModule from "@/store/modules/modal";
import { getPSSWithUUID } from "@/api/gql/product-slot-state";
import Timeout from "await-timeout";
import NotificationService from "@/services/notification.service";
import {
  generateBookingInvoice,
  getDownloadBookingTickets,
  getDownloadTicket,
  postDownloadBookingTickets,
  downloadBookingInvoice,
} from "@/api/booking/booking";
import { getBadgePdf, getGiftcardPdf } from "@/api/gql/badges";
import { base64toPdfBlob } from "@/utils/vue-helpers/badges";
import {
  InformationType,
  MEMBERSHIP_TICKETS,
  PCTypes,
} from "@/types/db/product-category";
import { getBookingWithUUID } from "@/api/gql/product-slot-boooking";
import POSConfigModule from "@/store/modules/pos-config";
import { useI18n } from "vue-i18n";
import { hasBookingChanged } from "@/utils/vue-helpers/booking";
import { LogService } from "@/services/logging";
import { isMembershipTicket } from "@/utils/vue-helpers/product-slot-state";
import i18n from "@/lang";
import { isBookingInDexie } from "@/utils/helpers/booking";
import { hasInvoiceEnabled } from "@/utils/vue-helpers/compliance";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";
import {
  DownloadIcon,
  IdCardIcon,
  LoaderCircleIcon,
  XIcon,
} from "@smeetz/ui-icons";

export default defineComponent({
  name: ModalComponents.DownloadTickets,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogHeader,
    SDialogTitle,
    DownloadIcon,
    IdCardIcon,
    LoaderCircleIcon,
    XIcon,
  },
  setup() {
    const { t } = useI18n({});
    const selectedBooking = computed(() => {
      return BookingModule.selectedBooking;
    });
    const modalVisible = dialogVisibleForStatus(ModalStatus.DownloadTickets);
    const ticektsInfo = ref<
      {
        pss: IProductSlotState;
        firstName: string;
        lastName: string;
      }[]
    >([]);
    const currency = ref(selectedBooking.value?.currency);
    const pssList = selectedBooking.value?.pssList || [];
    const loading = ref<boolean>();
    const bookingInfo = ref(null);

    for (let i = 0; i < pssList.length; i++) {
      if (
        pssList[i].bookingStatus !== BookingStatus.Confirmed ||
        pssList[i].pssType === PSSType[PSSType.Retail]
      ) {
        continue;
      }
      const pss: IProductSlotState = pssList[i];
      let firstName = selectedBooking.value?.firstName || "";
      let lastName = selectedBooking.value?.lastName || "";
      if (pss.customFieldsAnswers && pss.customFieldsAnswers?.length) {
        for (let j = 0; j < pss.customFieldsAnswers?.length; j++) {
          const customFieldType =
            pss.customFieldsAnswers[j].customField.informationType;
          if (
            customFieldType === InformationType.FIRSTNAME &&
            pss.customFieldsAnswers[j].answer
          ) {
            firstName = pss.customFieldsAnswers[j].answer;
          }
          if (
            customFieldType === InformationType.LASTNAME &&
            pss.customFieldsAnswers[j].answer
          ) {
            lastName = pss.customFieldsAnswers[j].answer;
          }
        }
      }
      ticektsInfo.value.push({
        pss,
        firstName,
        lastName,
      });
    }
    return {
      t,
      modalVisible,
      BookingStatus,
      ticektsInfo,
      currency,
      selectedBooking,
      loading,
      PCTypes,
      bookingInfo,
      MEMBERSHIP_TICKETS,
      isMembershipTicket,
      hasInvoiceEnabled,
    };
  },
  async mounted() {
    // keep showing the spinner until the booking gets synced
    this.loading = true;
    const uid = BookingModule.selectedBookingUUID;
    if (!uid) {
      throw new Error("No uuid for this booking");
    }
    let bookingInfo;
    let retries = 0;
    while (!bookingInfo && retries < 5) {
      bookingInfo = await getBookingWithUUID(uid);
      LogService.info(
        `Booking with uuid: ${uid} was not found when downloading tickets, retrying in 3000ms`
      );
      retries++;
      await Timeout.set(3000);
    }
    if (!bookingInfo) {
      // synching failed
      LogService.info(
        `Booking with uuid: ${uid} was not found when downloading tickets, stopped retrying`
      );
      NotificationService.showGeneralResponseError();
      this.closeModal();
      return;
    }
    this.bookingInfo = bookingInfo;
    this.loading = false;
  },
  methods: {
    closeModal() {
      ModalModule.setStatus(ModalStatus.Hidden);
      this.ticektsInfo = [];
    },
    saveFile(url: string) {
      const uniqueWindowName = `fileDownload_${Date.now()}`;
      window.open(url, uniqueWindowName);
    },
    async downloadTicket(pss: IProductSlotState) {
      this.loading = true;
      let pssInfo;

      // Check if pssId and bookingId are already available
      if (pss.id && this.selectedBooking.id) {
        pssInfo = { Id: pss.id, BookingId: this.selectedBooking.id };
      } else {
        // Repeat request 3 times
        let repeat = 0;
        while (repeat++ < 3) {
          pssInfo = await getPSSWithUUID(String(pss.uuid));
          if (pssInfo != null) break;
          await Timeout.set(3000);
        }

        // If pssInfo is still not available, show an error and return
        if (!pssInfo || !pssInfo.BookingId || !pssInfo.Id) {
          NotificationService.showGeneralResponseError();
          this.loading = false;
          throw new Error(
            `Booking with ${this.selectedBooking.uuid} not synced`
          );
        }
      }

      const url = await getDownloadTicket(
        Number(pssInfo.Id),
        pssInfo.BookingId
      );
      if (!url) {
        this.loading = false;
        NotificationService.showCustomError(this.t("badge.unknown-error"));
        throw new Error("Error downloading the ticket");
      }
      this.saveFile(url);
      this.loading = false;
    },
    async downloadAllTickets() {
      this.loading = true;
      await postDownloadBookingTickets(this.bookingInfo.id);
      const url = await getDownloadBookingTickets(this.bookingInfo.id);
      if (!url) {
        this.loading = false;
        NotificationService.showCustomError(this.t("badge.unknown-error"));
        throw new Error("Error downloading the tickets");
      }
      this.saveFile(url);
      this.loading = false;
    },
    async downloadBadge(pss: IProductSlotState) {
      this.loading = true;
      // Check this pss is fully paid or not and show a warning if it's not
      const pssListBalances = BookingModule.pssListBalances;
      let pssBalance = null;
      for (let i = 0; i < pssListBalances.length; i++) {
        if (pssListBalances[i].pssUuid === pss.uuid) {
          pssBalance = pssListBalances[i];
          break;
        }
      }
      if (pssBalance && pssBalance.balance < 0) {
        NotificationService.showCustomMessage(
          this.t("badge.not-fully-paid"),
          "warning"
        );
      }

      // For Locarno we have a special Badge layout
      const isLocarno =
        (POSConfigModule.groupId &&
          [19017 /*Locarno*/].includes(POSConfigModule.groupId)) ||
        false;
      try {
        const base64pdf = await getBadgePdf({
          pssId: pss.id,
          pssUuid: pss.uuid,
          isLocarno,
        });
        // download the generated pdf file
        const blob = base64toPdfBlob(base64pdf);
        const url = URL.createObjectURL(blob);
        this.saveFile(url);
        NotificationService.showCustomSuccess(
          this.t("completeorder.download-badge-success")
        );
        this.loading = false;
      } catch (error: any) {
        let errorMessage = "";
        switch (error.message) {
          case "Ticket Id or Uuid must be provided.":
            errorMessage = this.t("badge.invalid-ticket");
            break;
          case "Ticket not found!":
            errorMessage = this.t("badge.ticket-not-found");
            break;
          case "The requested ticket is not a membership":
            errorMessage = this.t("badge.not-membership ");
            break;
          case "Image linked to this membership not found":
            errorMessage = this.t("badge.image-not-found");
            break;
          case "This membership is cancelled.":
            errorMessage = this.t("badge.cancelled");
            break;
          case "Customer picture linked to this membership is not valid or not found":
            errorMessage = this.t("badge.picture-missing");
            break;
          default:
            errorMessage = this.t("badge.unknown-error");
            break;
        }
        NotificationService.showCustomError(errorMessage);
        this.loading = false;
      }
    },
    async downloadGiftcard(pss: IProductSlotState) {
      this.loading = true;
      try {
        const base64pdf = await getGiftcardPdf({
          pssId: pss.id,
          pssUuid: pss.uuid,
        });
        // download the generated pdf file
        const blob = base64toPdfBlob(base64pdf);
        const url = URL.createObjectURL(blob);
        this.saveFile(url);
        NotificationService.showCustomSuccess(
          this.t("completeorder.download-badge-success")
        );
        this.loading = false;
      } catch (error: any) {
        let errorMessage = "";
        switch (error.message) {
          case "Ticket Id or Uuid must be provided.":
            errorMessage = this.t("badge.invalid-ticket");
            break;
          case "Ticket not found!":
            errorMessage = this.t("badge.ticket-not-found");
            break;
          case "This giftcard is cancelled.":
            errorMessage = this.t("badge.cancelled");
            break;
          default:
            errorMessage = this.t("badge.unknown-error");
            break;
        }
        NotificationService.showCustomError(errorMessage);
        this.loading = false;
      }
    },
    async downloadInvoice() {
      const uid = BookingModule.selectedBookingUUID;
      if (!uid) {
        throw new Error("No uuid for this booking");
      }
      this.loading = true;
      // if the booking does not have an invoice yet, or it has been updated
      // we need to generate a new invoice
      const isEditOrder = await isBookingInDexie(uid);
      const bookingChanged = await hasBookingChanged(
        BookingModule.selectedBooking
      );
      if (!this.bookingInfo?.invoiceId || (isEditOrder && bookingChanged)) {
        await generateBookingInvoice(Number(this.bookingInfo?.id));
      }
      const url = await downloadBookingInvoice(this.bookingInfo.id);
      if (!url) {
        this.loading = false;
        LogService.info(`Error when downloading the invoice, booking: ${uid}`);
        NotificationService.showGeneralResponseError();
        throw new Error("Error downloading the invoice");
      }
      this.saveFile(url);
      this.loading = false;
    },
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.download-tickets-popup {
  position: relative;
}
.download-button {
  width: 40px;
  border-color: white;
  font-size: 30px;
  color: black;
  padding: 0;
}
.column {
  word-break: keep-all;
}

.spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .spinner {
    font-size: 48px;
    color: $color-dark-cyan;
  }
}
</style>
